import { json, type DataFunctionArgs } from '@remix-run/node'
import { useLoaderData, type MetaFunction, Link } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import ReactMarkdown from 'react-markdown'
import { Icon } from '#app/components/ui/icon.tsx'
import Breadcrumbs from '#app/components/Breadcrumbs/Breadcrumbs.tsx'

import { TProduct } from '#app/types/product/index.ts'
import { ISEProductCard } from '#app/components/ISEProductCard.tsx'

export async function loader({ params }: DataFunctionArgs) {
	const response = await fetch(
		`https://testservices.intersport.se/api/noselake/searcher/webstore/store?q=${params.store}`,
	)

	const store = (await response.json()) as any

	return json({
		store: store?.documentList?.documents[0],
		googleApiKey: 'AIzaSyCsKShHnZPLKQKsF_AQEAbJ6PwsLipjxbw',
	})
}

type IGoogleMap = {
	location: string
	apiKey: string
	address: string
}

const GoogleMap: React.FC<IGoogleMap> = ({ location, apiKey, address }) => (
	<a href={`https://maps.google.com/maps?q=Intersport,${address}`}>
		<img
			src={`https://maps.googleapis.com/maps/api/staticmap?size=640x450&zoom=13&scale=1&format=png&key=${apiKey}&map_id=62d6b9b3d16763a8&markers=size:normal%7Cscale:1%7Cicon:https://i.ibb.co/svR6Wjw/intersport-pin.png%7C${location}`}
			alt="Butikskarta"
			className="w-full"
		/>
	</a>
)

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const { store } = data

	const googleMapProps: IGoogleMap = {
		address: `${store.visitaddress},${store.visitzipcode} ${store.visitcity}`,
		apiKey: data.googleApiKey,
		location: `${data.store?.latitud},${data.store?.longitud}`,
	}

	return (
		<div className="container">
			<Breadcrumbs
				items={[
					{ href: '/', label: 'Startsida' },
					{ href: '..', label: 'Butiker' },
					{ href: '.', label: store.storenamefull },
				]}
				backItem={{ href: '..', label: store.storename }}
			/>
			<div className="uds-page-title uds-page-title__with-dot mt-md-7 mt-6">
				<h1 className="h-page">INTERSPORT {store?.storename}</h1>
			</div>
			<div className="row store-page align-items-start position-relative flex-md-row mt-md-7 mt-6">
				<div className="col-12 col-md-7">
					<div className="store__address flex flex-col gap-4 !text-sm leading-normal">
						<span>
							{store.visitaddress}, {store.visitzipcode} {store.visitcity}
						</span>
						<div className="flex flex-col gap-0">
							<span>Öppet idag: {store.openhours.comingdays[0]?.hours}</span>
							{Object.keys(store.openhours.regularhours).length !== 0 && (
								<>
									<span className="mt-4">
										Veckodagar: {store.openhours.regularhours.weekday}
									</span>
									<span>Lördagar: {store.openhours.regularhours.saturday}</span>
									<span>Söndagar: {store.openhours.regularhours.sunday}</span>
								</>
							)}
						</div>
						<div className="col-12 col-md-5 md:hidden">
							<GoogleMap {...googleMapProps} />
						</div>
					</div>
					<div className="mt-8 flex flex-row gap-8">
						<Link
							to={store?.storeno === "274" ? "http://boka.intersport.info/salen/forhandsbokning" : "boka"}
							className="flex gap-3 text-sm font-bold uppercase no-underline hover:text-secondary"
						>
							<Icon name="document-check" /> Boka
						</Link>
						<a
							href={`https://maps.google.com/maps?q=Intersport,${store.visitaddress},${store.visitzipcode} ${store.visitcity}`}
							className="flex gap-3 text-sm font-bold uppercase no-underline hover:text-secondary"
						>
							<Icon name="map-pin" /> Hitta hit
						</a>
						<Link
							to={`tel:${store.phonenumber}`}
							className="flex gap-3 text-sm font-bold uppercase no-underline hover:text-secondary"
						>
							<Icon name="phone" /> {store.phonenumber}
						</Link>
					</div>
					<div className="mt-8">
						<div className="mb-4 font-bold uppercase">Sortiment</div>
						<div className="text-sm font-medium">
							{store.extendedassortment.join(', ')}
						</div>
					</div>
					<div className="mt-8">
						<div className="mb-4 font-bold uppercase">Tjänster</div>
						<div className="text-sm font-medium">
							{store.extendedservices.join(', ')}
						</div>
					</div>
					<div className="mt-8">
						<div className="font-base whitespace-pre-wrap text-sm">
							<ReactMarkdown children={store.storeinfo} />
						</div>
					</div>
				</div>
				<div className="col-12 col-md-5 hidden md:block">
					<GoogleMap {...googleMapProps} />
				</div>
			</div>
			{/* <pre>{JSON.stringify(data.store, null, 2)}</pre> */}
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.store?.storename
	return [
		{ title: `${displayName} | INTERSPORT Rent` },
		{
			name: 'description',
			content: `Hyr dina skidor hos ${displayName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>Hittar inte någon butik som heter "{params.store}".</p>
				),
			}}
		/>
	)
}
